/**
 * Authenticated user-state cache.
 *
 * Supabase `user_state` is authoritative. The browser keeps only an in-memory
 * cache so screens that were originally written against localStorage can keep
 * synchronous get/set semantics after the authenticated startup hydration.
 * Anonymous reads are empty and anonymous writes are ignored: signed-out or
 * misconfigured sessions can never create state that later appears in the app.
 *
 * Older releases wrote `u:<uuid>:<key>` records to localStorage. On the first
 * authenticated hydration we migrate only records already tied to that exact
 * verified UUID, then remove the plaintext browser copies. Raw legacy/anonymous
 * keys are deliberately never claimed by a user.
 */
import { supabase, isSupabaseConfigured } from './supabaseClient';

const LEGACY_PREFIX = (userId) => `u:${userId}:`;
const SYNC_ERROR_EVENT = 'prayer-guide:sync-error';
const SYNC_STATUS_EVENT = 'prayer-guide:sync-status';
const UNSCOPED_PRIVATE_KEYS = new Set([
    'prayer_guides',
    'prayer_app_journal',
    'prayer_app_requests',
    'prayer_app_settings',
    'prayer_calendar_events_v2',
    'prayerTopics',
    'prayerGarden',
    'prayer_requests',
    'memory_verses',
    'verse_review_dates',
    'bible_reading_progress',
    'prayer_app_devotionals',
    'prayer_app_favorite_devotionals',
    'bible_reader_bookmarks',
    'bible_reader_highlights',
    'bible_reader_notes',
    'bible_reader_history',
    'bible_reader_seen_verses',
    'bible_reader_preferences',
    'prayer_guide_premium_preference',
    'verse-delivery-subscriptions-v1',
    'verse-delivery-deliveries-v1'
]);
const UNSCOPED_PRIVATE_PREFIXES = [
    'prayer_app_daily_prayer_',
    'daily_verse_reflection_'
];
const cloudEnabled = () => isSupabaseConfigured && process.env.NODE_ENV !== 'test';

let currentUserId = null;
const userCaches = new Map();
const writeQueues = new Map();
const failedWrites = new Map();

const cacheFor = (userId, create = true) => {
    if (!userId) return null;
    if (!userCaches.has(userId) && create) userCaches.set(userId, new Map());
    return userCaches.get(userId) || null;
};

const serializeCloudValue = (value) => (
    typeof value === 'string' ? value : JSON.stringify(value)
);

const reportSyncError = (error) => {
    console.warn('[userStorage] cloud sync failed:', error.message);
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(SYNC_ERROR_EVENT, {
            detail: { message: 'A recent change could not be saved to your account yet.' }
        }));
    }
};

const reportSyncStatus = (state) => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(SYNC_STATUS_EVENT, {
            detail: { state, pending: writeQueues.size, failed: failedWrites.size }
        }));
    }
};

const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const runCloudOperation = async (operation, attempts = 3) => {
    let lastError;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
            const { error } = await operation();
            if (error) throw error;
            return true;
        } catch (error) {
            lastError = error;
            if (attempt < attempts - 1) await delay(200 * (2 ** attempt));
        }
    }
    throw lastError;
};

const queueKey = (userId, key) => `${userId}:${key}`;

const enqueueCloudWrite = (userId, key, operation) => {
    if (!cloudEnabled() || !userId) return Promise.resolve();
    const id = queueKey(userId, key);
    // A newer whole-key write supersedes a previously failed one for this key.
    failedWrites.delete(id);
    const previous = writeQueues.get(id) || Promise.resolve();
    const next = previous
        .catch(() => undefined)
        .then(() => runCloudOperation(operation))
        .catch((error) => {
            // Do not retain an obsolete failure when a newer operation for the
            // same key is already queued behind it.
            if (writeQueues.get(id) === next) {
                failedWrites.set(id, { userId, key, operation, error });
            }
            reportSyncError(error);
            return false;
        })
        .finally(() => {
            if (writeQueues.get(id) === next) writeQueues.delete(id);
            reportSyncStatus(failedWrites.size ? 'error' : writeQueues.size ? 'syncing' : 'synced');
        });
    writeQueues.set(id, next);
    reportSyncStatus('syncing');
    return next;
};

const cloudUpsert = (userId, key, value) => enqueueCloudWrite(userId, key, () => (
    supabase
        .from('user_state')
        .upsert({ user_id: userId, key, value }, { onConflict: 'user_id,key' })
));

const cloudDelete = (userId, key) => enqueueCloudWrite(userId, key, () => (
    supabase
        .from('user_state')
        .delete()
        .match({ user_id: userId, key })
));

export const setStorageUser = (userId) => {
    currentUserId = userId || null;
    if (currentUserId) cacheFor(currentUserId);
};

export const getStorageUser = () => currentUserId;

export const flushPendingWrites = async (userId = currentUserId) => {
    if (!userId) return;
    const prefix = `${userId}:`;
    // Drain writes added while an earlier batch was being awaited.
    for (let pass = 0; pass < 4; pass += 1) {
        const pending = [...writeQueues.entries()]
            .filter(([key]) => key.startsWith(prefix))
            .map(([, promise]) => promise);
        if (!pending.length) break;
        await Promise.all(pending);
    }

    const failures = [...failedWrites.entries()].filter(([key]) => key.startsWith(prefix));
    for (const [id, failed] of failures) {
        try {
            await runCloudOperation(failed.operation, 2);
            if (failedWrites.get(id) === failed) failedWrites.delete(id);
        } catch (error) {
            if (failedWrites.get(id) === failed) failedWrites.set(id, { ...failed, error });
        }
    }

    const remainingFailures = [...failedWrites.keys()].filter((key) => key.startsWith(prefix)).length;
    const remainingPending = [...writeQueues.keys()].filter((key) => key.startsWith(prefix)).length;
    const remaining = remainingFailures + remainingPending;
    if (remaining) {
        reportSyncStatus('error');
        throw new Error(`${remaining} recent change${remaining === 1 ? '' : 's'} could not be saved to your account.`);
    }
    reportSyncStatus('synced');
};

export const hasUnsyncedWrites = (userId = currentUserId) => {
    if (!userId) return false;
    const prefix = `${userId}:`;
    return [...writeQueues.keys(), ...failedWrites.keys()].some((key) => key.startsWith(prefix));
};

const legacyStorageAreas = () => {
    if (typeof window === 'undefined') return [];
    return [window.localStorage, window.sessionStorage];
};

export const purgeUnscopedBrowserState = () => {
    legacyStorageAreas().forEach((storage) => {
        Object.keys(storage)
            .filter((key) => (
                UNSCOPED_PRIVATE_KEYS.has(key)
                || UNSCOPED_PRIVATE_PREFIXES.some((prefix) => key.startsWith(prefix))
            ))
            .forEach((key) => storage.removeItem(key));
    });
};

// Old single-user builds left private values without an account namespace.
// Delete those values on every startup; they are never offered to a login.
purgeUnscopedBrowserState();

const removeLegacyBrowserCache = (userId) => {
    const prefix = LEGACY_PREFIX(userId);
    legacyStorageAreas().forEach((storage) => {
        Object.keys(storage)
            .filter((key) => key.startsWith(prefix))
            .forEach((key) => storage.removeItem(key));
    });
};

export const clearUserCache = (userId = currentUserId) => {
    if (!userId) return;
    userCaches.delete(userId);
    const prefix = `${userId}:`;
    [...failedWrites.keys()].filter((key) => key.startsWith(prefix)).forEach((key) => failedWrites.delete(key));
    [...writeQueues.keys()].filter((key) => key.startsWith(prefix)).forEach((key) => writeQueues.delete(key));
    removeLegacyBrowserCache(userId);
    if (currentUserId === userId) currentUserId = null;
    reportSyncStatus(failedWrites.size ? 'error' : writeQueues.size ? 'syncing' : 'synced');
};

export const scopedStorage = {
    getItem(key) {
        const cache = cacheFor(currentUserId, false);
        return cache?.has(key) ? cache.get(key) : null;
    },
    setItem(key, value) {
        if (!currentUserId) return;
        const stringValue = String(value);
        cacheFor(currentUserId).set(key, stringValue);
        cloudUpsert(currentUserId, key, stringValue);
    },
    removeItem(key) {
        if (!currentUserId) return;
        cacheFor(currentUserId)?.delete(key);
        cloudDelete(currentUserId, key);
    },
    clear() {
        if (!currentUserId) return;
        const userId = currentUserId;
        cacheFor(userId)?.clear();
        if (cloudEnabled()) {
            enqueueCloudWrite(userId, '*', () => (
                supabase.from('user_state').delete().eq('user_id', userId)
            ));
        }
    }
};

/**
 * Load the signed-in user's authoritative state before the routed tree mounts.
 * Cloud values win. UUID-scoped browser data from the previous implementation
 * is imported only for keys that do not exist in cloud storage.
 */
export const hydrateFromCloud = async (userId) => {
    if (!userId) throw new Error('A verified user is required to load app data.');

    if (!cloudEnabled()) {
        cacheFor(userId);
        return;
    }

    const { data, error } = await supabase
        .from('user_state')
        .select('key,value')
        .eq('user_id', userId);
    if (error) throw error;

    const hydrated = new Map((data || []).map((row) => [
        row.key,
        serializeCloudValue(row.value)
    ]));
    const prefix = LEGACY_PREFIX(userId);
    const migratedKeys = [];

    legacyStorageAreas().forEach((storage) => {
        Object.keys(storage)
            .filter((storageKey) => storageKey.startsWith(prefix))
            .forEach((storageKey) => {
                const key = storageKey.slice(prefix.length);
                if (!hydrated.has(key)) {
                    const value = storage.getItem(storageKey);
                    if (value !== null) {
                        hydrated.set(key, value);
                        migratedKeys.push([key, value]);
                    }
                }
            });
    });

    userCaches.set(userId, hydrated);
    const migrationResults = await Promise.all(
        migratedKeys.map(([key, value]) => cloudUpsert(userId, key, value))
    );
    if (migrationResults.every(Boolean)) removeLegacyBrowserCache(userId);
};

export { SYNC_ERROR_EVENT, SYNC_STATUS_EVENT };
export default scopedStorage;
